import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm' : 'bg-white/80'}`}>
        <div className="max-w-7xl mx-auto px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://public.readdy.ai/ai/img_res/d6aa2f39-c38d-49ea-8b8b-ecf0253c97d5.png" alt="DUWAR Logo" className="h-10 w-10 object-contain" />
            <span className="text-2xl font-bold text-gray-900">DUWAR d.o.o.</span>
          </div>
          <div className="flex items-center gap-12">
            <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-teal-600 transition-colors font-medium cursor-pointer whitespace-nowrap">Home</button>
            <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-teal-600 transition-colors font-medium cursor-pointer whitespace-nowrap">About</button>
            <button onClick={() => scrollToSection('products')} className="text-gray-700 hover:text-teal-600 transition-colors font-medium cursor-pointer whitespace-nowrap">Products</button>
            <button onClick={() => scrollToSection('features')} className="text-gray-700 hover:text-teal-600 transition-colors font-medium cursor-pointer whitespace-nowrap">Features</button>
            <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-teal-600 transition-colors font-medium cursor-pointer whitespace-nowrap">Contact</button>
          </div>
        </div>
      </nav>

      {/* Hero Section - Home */}
      <section id="home" className="relative pt-32 pb-24 overflow-hidden bg-gradient-to-br from-white via-gray-50 to-slate-100">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <h1 className="text-7xl font-thin text-gray-900 leading-tight">
                Crafting Premium<br />iOS Experiences
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-lg">
                We develop cutting-edge applications that redefine mobile security and performance on the iOS platform.
              </p>
              <div className="inline-block bg-white px-8 py-4 rounded-2xl shadow-lg">
                <p className="text-lg font-semibold text-gray-900">VPN & Cleaner Specialists</p>
              </div>
            </div>
            <div className="relative h-[600px] flex items-center justify-center">
              <div className="absolute inset-0 flex items-center justify-center">
                <img 
                  src="https://public.readdy.ai/ai/img_res/586bde5f1d5d31b7231a9f7590d31673.jpg" 
                  alt="iOS App Interface" 
                  className="w-full h-full object-contain transform rotate-6 hover:rotate-3 transition-transform duration-500"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="mb-8">
            <span className="inline-block px-6 py-2 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">Who We Are</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-6xl font-bold text-gray-900 leading-tight">
                A Team of<br />iOS Development<br />Experts
              </h2>
            </div>
            <div className="space-y-8">
              <div className="bg-gradient-to-br from-emerald-900 to-teal-800 rounded-3xl p-12 shadow-xl">
                <p className="text-white text-lg leading-relaxed mb-6">
                  DUWAR d.o.o. is a dedicated team of professionals specializing in iOS application development. We combine technical excellence with creative innovation to deliver products that stand out in the App Store.
                </p>
                <p className="text-white/90 text-lg leading-relaxed">
                  Our expertise spans security solutions, system optimization, and user experience design. Every application we create is built with meticulous attention to detail and a commitment to quality.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-8">
          <h2 className="text-7xl font-thin text-gray-900 mb-20">Our Products</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* VPN Product Card */}
            <div className="bg-white rounded-3xl shadow-lg overflow-hidden transform hover:-translate-y-2 transition-transform duration-300">
              <div className="h-80 bg-gradient-to-br from-blue-500 via-purple-500 to-indigo-600 flex items-center justify-center relative overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=abstract%20VPN%20security%20shield%20icon%20with%20encrypted%20connection%20visualization%20modern%20digital%20interface%20design%20featuring%20network%20nodes%20and%20secure%20pathways%20blue%20purple%20gradient%20background%20minimalist%20tech%20illustration%20clean%20professional%20style%20with%20glowing%20effects%20cybersecurity%20concept%20art&width=600&height=400&seq=vpn-product-001&orientation=landscape" 
                  alt="VPN Product" 
                  className="w-full h-full object-cover opacity-90"
                />
              </div>
              <div className="p-10 text-center">
                <h3 className="text-3xl font-semibold text-gray-900 mb-4">VPN Application</h3>
                <p className="text-gray-600 leading-relaxed">
                  Secure your online presence with our advanced VPN solution. Military-grade encryption, lightning-fast servers, and seamless iOS integration ensure your privacy is always protected.
                </p>
              </div>
            </div>

            {/* Cleaner Product Card */}
            <div className="bg-white rounded-3xl shadow-lg overflow-hidden transform hover:-translate-y-2 transition-transform duration-300 lg:mt-16">
              <div className="h-80 bg-gradient-to-br from-orange-400 via-pink-500 to-rose-500 flex items-center justify-center relative overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20phone%20cleaner%20optimization%20interface%20showing%20storage%20management%20and%20performance%20boost%20features%20clean%20minimalist%20design%20with%20circular%20progress%20indicators%20orange%20pink%20gradient%20background%20professional%20mobile%20app%20visualization%20sleek%20UI%20elements%20system%20optimization%20concept&width=600&height=400&seq=cleaner-product-001&orientation=landscape" 
                  alt="Cleaner Product" 
                  className="w-full h-full object-cover opacity-90"
                />
              </div>
              <div className="p-10 text-center">
                <h3 className="text-3xl font-semibold text-gray-900 mb-4">Cleaner Application</h3>
                <p className="text-gray-600 leading-relaxed">
                  Optimize your device performance with intelligent cleaning algorithms. Remove junk files, manage storage efficiently, and keep your iPhone running at peak performance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-right mb-20">
            <h2 className="text-6xl font-serif text-gray-900">[ What We Deliver ]</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-blue-50 rounded-3xl p-10 h-96 flex flex-col items-center justify-center text-center space-y-6">
              <div className="w-16 h-16 flex items-center justify-center">
                <i className="ri-shield-check-line text-6xl text-blue-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Enhanced Security</h3>
              <p className="text-gray-600 leading-relaxed">
                Military-grade encryption and advanced security protocols protect your data and privacy at all times.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-gray-50 rounded-3xl p-10 h-[28rem] flex flex-col items-center justify-center text-center space-y-6">
              <div className="w-16 h-16 flex items-center justify-center">
                <i className="ri-dashboard-line text-6xl text-gray-700"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Optimized Performance</h3>
              <p className="text-gray-600 leading-relaxed">
                Intelligent algorithms ensure your device runs smoothly with maximum efficiency and minimal resource usage.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-gray-50 rounded-3xl p-10 h-96 flex flex-col items-center justify-center text-center space-y-6">
              <div className="w-16 h-16 flex items-center justify-center">
                <i className="ri-user-heart-line text-6xl text-gray-700"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">User-Centric Design</h3>
              <p className="text-gray-600 leading-relaxed">
                Intuitive interfaces and seamless experiences make our apps accessible to users of all technical levels.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-gray-50">
        <div className="max-w-5xl mx-auto px-8 text-center">
          <h2 className="text-6xl font-bold text-gray-900 mb-6">Ready to Enhance Mobile Security?</h2>
          <p className="text-xl text-gray-600 mb-16 max-w-2xl mx-auto">
            Join thousands of users who trust DUWAR applications for their iOS security and optimization needs.
          </p>
          <div className="relative h-64 flex items-center justify-center gap-8">
            <img 
              src="https://readdy.ai/api/search-image?query=row%20of%20modern%20iPhones%20displaying%20security%20and%20cleaner%20app%20interfaces%20arranged%20with%20perspective%20tilt%20professional%20product%20showcase%20clean%20white%20background%20with%20subtle%20shadows%20premium%20technology%20presentation%20multiple%20devices%20showing%20different%20app%20screens%20sleek%20minimalist%20composition&width=1200&height=400&seq=cta-phones-001&orientation=landscape" 
              alt="App Showcase" 
              className="w-full h-full object-contain"
            />
          </div>
        </div>
      </section>

      {/* Contact Footer */}
      <section id="contact" className="py-16 px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-16 relative overflow-hidden">
            <div className="absolute bottom-0 right-0 text-9xl font-bold text-slate-800/20 select-none pointer-events-none">
              DUWAR
            </div>
            <div className="relative z-10 grid grid-cols-1 md:grid-cols-4 gap-12">
              {/* Column 1 */}
              <div>
                <h3 className="text-5xl font-bold text-white mb-8">Get in Touch</h3>
              </div>

              {/* Column 2 - Location */}
              <div>
                <h4 className="text-xl font-semibold text-white mb-6">Location</h4>
                <div className="space-y-3 text-gray-300 text-sm">
                  <div>
                    <p className="text-gray-400 mb-1">Address</p>
                    <p>Bjelovarska cesta 18</p>
                  </div>
                  <div>
                    <p className="text-gray-400 mb-1">City</p>
                    <p>Koprivnica</p>
                  </div>
                  <div>
                    <p className="text-gray-400 mb-1">Zip</p>
                    <p>48000</p>
                  </div>
                  <div>
                    <p className="text-gray-400 mb-1">Country</p>
                    <p>Croatia</p>
                  </div>
                </div>
              </div>

              {/* Column 3 - Connect */}
              <div>
                <h4 className="text-xl font-semibold text-white mb-6">Connect</h4>
                <div className="space-y-3 text-gray-300 text-sm">
                  <div>
                    <p className="text-gray-400 mb-1">Email</p>
                    <p>contact@duwardoo.com</p>
                  </div>
                  <div>
                    <p className="text-gray-400 mb-1">Number</p>
                    <p>+1 5734902090</p>
                  </div>
                </div>
              </div>

              {/* Column 4 - Company Info */}
              <div>
                <h4 className="text-xl font-semibold text-white mb-6">Company</h4>
                <div className="space-y-3 text-gray-300 text-sm">
                  <p>DUWAR d.o.o.</p>
                  <p className="text-gray-400">iOS Development Specialists</p>
                  <p className="text-gray-400 mt-6">© 2025 All rights reserved</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Link Footer */}
      <footer className="py-6 bg-white">
        <div className="max-w-7xl mx-auto px-8 text-center">
          <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-500 hover:text-teal-600 transition-colors cursor-pointer">
            Powered by Readdy
          </a>
        </div>
      </footer>
    </div>
  );
}
